import ExcelJS, { type Cell, type Worksheet } from "exceljs";
import { COLUMN_HEADER_FILL, THIN_BORDER } from "../../../../downloads/dailyPrintout/render/cellStyle";
import { PAPER_SIZE_A4 } from "../../../../utils/excelPageSetup";
import { hexToArgb } from "../../../../utils/excelToFortuneSheet";
import type { GapRowType, GridDates } from "../types";
import { EMPTY_GAP_CELL, type GapCellContext, gapCellText, resolveGapCell } from "../utils/gapCells";
import { gapToneStyle } from "../utils/gapTones";
import { isUncountedRow } from "../utils/metricRows";
import { SLATE } from "../utils/styleTokens";
import { cellKey, DAYS_PER_WEEK } from "../utils/suggestedHires";
import { columnToDayIdx } from "../utils/weekStart";

/** Everything the grid is drawing right now — its rows already narrowed by the view settings. */
export type RecurringGapsExportView = {
	dates: GridDates;
	weekNumbers: number[];
	weekStartDay: number;
	gridWeeks: number;
	/** The grid's own cell context, so every cell is read exactly as the grid reads it. */
	cellContext: GapCellContext;
	/** The shift blocks on screen, in screen order — the shift filter already applied. */
	shiftLabels: string[];
	/** The rows inside each shift block, from `gapsRowTypes` — Detailed View and Show already applied. */
	rowTypes: GapRowType[];
	/** `null` when the grid isn't drawing the Template HPPD row. */
	templateHppd: Record<number, number> | null;
};

/** Every user-facing string the sheet prints, already translated by the caller. */
export type RecurringGapsExportLabels = {
	sheetName: string;
	title: string;
	subtitle: string;
	legend: string;
	shiftHeader: string;
	rowHeader: string;
	templateHppd: string;
	weekHeader: (weekIdx: number) => string;
	/** The weekday name over a column, from the column's Sunday-based `dayIdx`. */
	dayHeader: (dayIdx: number) => string;
	rowLabel: (rowType: GapRowType) => string;
};

/** Shift + row-label columns before the day columns start. */
const LEADING_COLUMNS = 2;
const HEADER_ROWS = 5;
const WEEK_HEADER_ROW = 4;
const DAY_HEADER_ROW = 5;
const TITLE_FONT_SIZE = 14;

const SHIFT_COLUMN_WIDTH = 16;
const ROW_LABEL_COLUMN_WIDTH = 16;
const DAY_COLUMN_WIDTH = 7;

/**
 * Missing is stored as a NUMBER (the surplus: `-3` short, `+2` over) so it still sums and sorts, and this
 * format prints it exactly as the grid does — signed, with a check for a day that is exactly covered.
 */
const MISSING_NUMBER_FORMAT = '+0;-0;"✓"';
const HPPD_NUMBER_FORMAT = "0.00";

const MUTED_ARGB = hexToArgb(SLATE[400]);
/** The grid dims a shown-but-uncounted row to half opacity; the file can't, so it halves the tint instead. */
const DIMMED_ROW_OPACITY = 0.5;
const RGBA_HEX_PATTERN = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;
const CHANNEL_MAX = 255;

/**
 * A translucent `#RRGGBBAA` tint as the solid colour it shows on the grid's white background. An Excel
 * fill has no alpha, and writing the colour opaque would print every breakdown cell darker than on screen.
 */
const tintOverWhiteArgb = (rgbaHex: string, opacity: number): string => {
	const match = RGBA_HEX_PATTERN.exec(rgbaHex);
	if (!match) {
		throw new Error(`Recurring Gaps export: ${rgbaHex} is not an #RRGGBBAA tint`);
	}
	const alpha = (Number.parseInt(match[4], 16) / CHANNEL_MAX) * opacity;
	const channels = [match[1], match[2], match[3]].map((channel) =>
		Math.round(Number.parseInt(channel, 16) * alpha + CHANNEL_MAX * (1 - alpha))
			.toString(16)
			.padStart(2, "0"),
	);
	return hexToArgb(`#${channels.join("")}`);
};
const WEEK_HEADER_FILL = { type: "pattern" as const, pattern: "solid" as const, fgColor: { argb: hexToArgb(SLATE[800]) } };
const WHITE_ARGB = "FFFFFFFF";

const PAGE_MARGINS_INCHES = { left: 0.3, right: 0.3, top: 0.5, bottom: 0.5, header: 0.3, footer: 0.3 };

type CellContent = { value: string | number; numFmt?: string; fontArgb?: string; fillArgb?: string; bold: boolean };

const lastColumn = (view: RecurringGapsExportView): number => LEADING_COLUMNS + view.gridWeeks * DAYS_PER_WEEK;

const styleCell = (cell: Cell, content: CellContent): void => {
	cell.value = content.value;
	cell.border = THIN_BORDER;
	cell.alignment = { horizontal: "center", vertical: "middle" };
	cell.font = { bold: content.bold, ...(content.fontArgb ? { color: { argb: content.fontArgb } } : {}) };
	if (content.numFmt) cell.numFmt = content.numFmt;
	if (content.fillArgb) cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: content.fillArgb } };
};

/**
 * One cell, styled for the file. What it SAYS comes from `resolveGapCell` / `gapCellText`, the same
 * functions the grid draws from; Missing is written as the number behind the grid's signed text.
 */
const resolveContent = (view: RecurringGapsExportView, rowType: GapRowType, label: string, key: number, dimmed: boolean): CellContent => {
	const cell = resolveGapCell(view.cellContext, rowType, label, key);
	if (cell.kind === "missing") {
		const style = gapToneStyle(cell.tone);
		return {
			value: -cell.missing,
			numFmt: MISSING_NUMBER_FORMAT,
			fontArgb: hexToArgb(style.ink),
			fillArgb: style.bg ? hexToArgb(style.bg) : undefined,
			bold: true,
		};
	}
	if (cell.kind === "needed") {
		return { value: gapCellText(cell), fontArgb: MUTED_ARGB, bold: false };
	}
	if (cell.kind === "recurring") {
		return { value: gapCellText(cell), bold: true };
	}
	if (cell.value <= 0) {
		return { value: gapCellText(cell), fontArgb: MUTED_ARGB, bold: false };
	}
	// A breakdown cell with a value wears its row's ink on its row's tint, as on the grid.
	return {
		value: gapCellText(cell),
		fontArgb: dimmed ? MUTED_ARGB : hexToArgb(cell.metric.color),
		fillArgb: tintOverWhiteArgb(cell.metric.bg, dimmed ? DIMMED_ROW_OPACITY : 1),
		bold: false,
	};
};

/** Calls `write` once per day column, left to right, with the grid's Sunday-based `dayIdx`. */
const forEachDayColumn = (view: RecurringGapsExportView, write: (column: number, weekIdx: number, dayIdx: number) => void): void => {
	for (let weekIdx = 0; weekIdx < view.gridWeeks; weekIdx++) {
		for (let column = 0; column < DAYS_PER_WEEK; column++) {
			write(LEADING_COLUMNS + weekIdx * DAYS_PER_WEEK + column + 1, weekIdx, columnToDayIdx(column, view.weekStartDay));
		}
	}
};

const writeBanner = (worksheet: Worksheet, row: number, view: RecurringGapsExportView, value: string, font: Partial<ExcelJS.Font>): void => {
	worksheet.mergeCells(row, 1, row, lastColumn(view));
	const cell = worksheet.getCell(row, 1);
	cell.value = value;
	cell.font = font;
	cell.alignment = { horizontal: "left", vertical: "middle" };
};

const writeHeaderCell = (cell: Cell, value: string): void => {
	cell.value = value;
	cell.font = { bold: true };
	cell.fill = COLUMN_HEADER_FILL;
	cell.border = THIN_BORDER;
	cell.alignment = { horizontal: "center", vertical: "middle" };
};

const writeColumnHeaders = (worksheet: Worksheet, view: RecurringGapsExportView, labels: RecurringGapsExportLabels): void => {
	worksheet.mergeCells(WEEK_HEADER_ROW, 1, WEEK_HEADER_ROW, LEADING_COLUMNS);
	for (let weekIdx = 0; weekIdx < view.gridWeeks; weekIdx++) {
		const first = LEADING_COLUMNS + weekIdx * DAYS_PER_WEEK + 1;
		worksheet.mergeCells(WEEK_HEADER_ROW, first, WEEK_HEADER_ROW, first + DAYS_PER_WEEK - 1);
		const cell = worksheet.getCell(WEEK_HEADER_ROW, first);
		cell.value = labels.weekHeader(weekIdx);
		cell.font = { bold: true, color: { argb: WHITE_ARGB } };
		cell.fill = WEEK_HEADER_FILL;
		cell.alignment = { horizontal: "center", vertical: "middle" };
	}
	writeHeaderCell(worksheet.getCell(DAY_HEADER_ROW, 1), labels.shiftHeader);
	writeHeaderCell(worksheet.getCell(DAY_HEADER_ROW, 2), labels.rowHeader);
	forEachDayColumn(view, (column, _weekIdx, dayIdx) => writeHeaderCell(worksheet.getCell(DAY_HEADER_ROW, column), labels.dayHeader(dayIdx)));
};

/** One shift block: the shift label down the left, one row per row type. Returns the next free row. */
const writeShiftBlock = (worksheet: Worksheet, startRow: number, label: string, view: RecurringGapsExportView, labels: RecurringGapsExportLabels): number => {
	worksheet.mergeCells(startRow, 1, startRow + view.rowTypes.length - 1, 1);
	const shiftCell = worksheet.getCell(startRow, 1);
	shiftCell.value = label;
	shiftCell.font = { bold: true };
	shiftCell.border = THIN_BORDER;
	shiftCell.alignment = { horizontal: "center", vertical: "middle" };

	view.rowTypes.forEach((rowType, offset) => {
		const row = startRow + offset;
		const isMissing = rowType === "missing";
		// A row shown but not counted is greyed whole, label included — the grid dims the whole row.
		const dimmed = isUncountedRow(rowType, view.cellContext.includeFlags);
		const labelCell = worksheet.getCell(row, 2);
		labelCell.value = labels.rowLabel(rowType);
		labelCell.font = { bold: isMissing, ...(dimmed ? { color: { argb: MUTED_ARGB } } : {}) };
		labelCell.border = THIN_BORDER;
		labelCell.alignment = { horizontal: "left", vertical: "middle" };
		forEachDayColumn(view, (column, weekIdx, dayIdx) => {
			styleCell(worksheet.getCell(row, column), resolveContent(view, rowType, label, cellKey(weekIdx, dayIdx), dimmed));
		});
	});
	return startRow + view.rowTypes.length;
};

const writeTemplateHppdRow = (worksheet: Worksheet, row: number, hppd: Record<number, number>, view: RecurringGapsExportView, label: string): void => {
	worksheet.mergeCells(row, 1, row, LEADING_COLUMNS);
	const labelCell = worksheet.getCell(row, 1);
	labelCell.value = label;
	labelCell.font = { bold: true };
	labelCell.border = THIN_BORDER;
	labelCell.alignment = { horizontal: "center", vertical: "middle" };
	forEachDayColumn(view, (column, _weekIdx, dayIdx) => {
		const value = hppd[dayIdx];
		styleCell(
			worksheet.getCell(row, column),
			value === undefined ? { value: EMPTY_GAP_CELL, fontArgb: MUTED_ARGB, bold: true } : { value, numFmt: HPPD_NUMBER_FORMAT, bold: true },
		);
	});
};

const applyLayout = (worksheet: Worksheet, view: RecurringGapsExportView): void => {
	worksheet.columns = [
		{ width: SHIFT_COLUMN_WIDTH },
		{ width: ROW_LABEL_COLUMN_WIDTH },
		...Array.from({ length: view.gridWeeks * DAYS_PER_WEEK }, () => ({ width: DAY_COLUMN_WIDTH })),
	];
	// The shift and row names stay in view while scrolling across the weeks, as they do on screen.
	worksheet.views = [{ state: "frozen", xSplit: LEADING_COLUMNS, ySplit: HEADER_ROWS }];
	worksheet.pageSetup = {
		paperSize: PAPER_SIZE_A4,
		orientation: "landscape",
		fitToPage: true,
		fitToWidth: 1,
		fitToHeight: 0,
		margins: PAGE_MARGINS_INCHES,
		printTitlesRow: `${WEEK_HEADER_ROW}:${DAY_HEADER_ROW}`,
	};
};

/**
 * The Recurring Gaps grid as an Excel sheet — the same rows, numbers and gap colours the user is looking
 * at, so the file needs no explaining next to the screen it came from.
 */
export const createRecurringGapsWorkbook = async (view: RecurringGapsExportView, labels: RecurringGapsExportLabels): Promise<ArrayBuffer> => {
	if (view.weekNumbers.length < view.gridWeeks) {
		throw new Error(`Recurring Gaps export: ${view.gridWeeks} weeks but ${view.weekNumbers.length} week numbers`);
	}
	const workbook = new ExcelJS.Workbook();
	const worksheet = workbook.addWorksheet(labels.sheetName);
	applyLayout(worksheet, view);

	writeBanner(worksheet, 1, view, labels.title, { bold: true, size: TITLE_FONT_SIZE });
	writeBanner(worksheet, 2, view, labels.subtitle, { color: { argb: MUTED_ARGB } });
	writeColumnHeaders(worksheet, view, labels);

	let row = HEADER_ROWS + 1;
	for (const label of view.shiftLabels) {
		row = writeShiftBlock(worksheet, row, label, view, labels);
	}
	if (view.templateHppd) {
		writeTemplateHppdRow(worksheet, row, view.templateHppd, view, labels.templateHppd);
		row += 1;
	}
	writeBanner(worksheet, row + 1, view, labels.legend, { color: { argb: MUTED_ARGB } });

	// exceljs types this as its own Buffer alias; the browser download helper takes an ArrayBuffer, which
	// is what it actually returns in the browser.
	return (await workbook.xlsx.writeBuffer()) as ArrayBuffer;
};
